import React, { useState } from 'react';
import { Container, Box, Link, useTheme, useMediaQuery } from '@mui/material';
import { Button, Stepper, Typography } from '@sm-dashboard/ui-components';
import instanceAxios from '../../../util/axios';

import BusinessInfoForm from '../../../components/dashboard/BusinessAds/public/business-ad-submission/BusinessInfoForm';
import CategorySelector from '../../../components/dashboard/BusinessAds/public/business-ad-submission/CategorySelector';
import DescriptionAndMediaForm from '../../../components/dashboard/BusinessAds/public/business-ad-submission/DescriptionAndMediaForm';
import AdditionalInfoForm from '../../../components/dashboard/BusinessAds/public/business-ad-submission/AdditionalInfoForm';
import RegulationsAndApproval from '../../../components/dashboard/BusinessAds/public/business-ad-submission/RegulationsAndApproval';
import WhatsAppSupportButton from '../../../components/dashboard/BusinessAds/WhatsAppSupportButton';
import {
  SHALIACH_EXPLANATION_TEXT,
  SHALIACH_REGULATIONS_LINK,
  SHALIACH_REFERRAL_LINE_1,
  SHALIACH_REFERRAL_LINE_2_PREFIX,
  PUBLISHER_SUBMIT_PATH
} from '../../../constants/businessAds.constants';
import SubmissionSuccess from '../../../components/common/SubmissionSuccess';
import { businessAdsUrl } from '@/constants';

const initialFormData = {
  submitterType: 'shaliach' as 'publisher' | 'shaliach',
  businessName: '',
  businessNameForInvoicing: '',
  businessEmail: '',
  businessPhone: '',
  hasWhatsapp: true,
  category: '',
  subCategory: [] as string[],
  shortDescription: '',
  fullDescription: '',
  location: [] as string[],
  minPrice: '' as number | '',
  maxPrice: '' as number | '',
  targetAudience: [] as string[],
  shabadHouseDiscount: '',
  mainImage: '',
  additionalMedia: [] as string[],
  websiteLink: '',
  facebookLink: '',
  instagramLink: '',
  recommendations: '',
  paymentPlan: 'none'
};

const ShaliachAdSubmissionPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);

  const steps = ['פרטי העסק', 'קטגוריה', 'תיאור ומדיה', 'מידע נוסף', 'תקנון ואישור'];

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleReset = () => {
    setActiveStep(0);
    setFormData(initialFormData);
    setSuccess(false);
    setError('');
    setAgreed(false);
  };

  // Validation helper functions
  const isValidEmail = (email: string) => {
    if (!email) return false; // Email is now required
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string) => {
    const phoneRegex = /^[0-9]{10}$|^0[0-9]{1,2}-?[0-9]{7}$|^\+972-?[0-9]{1,2}-?[0-9]{7}$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Remove empty string values for optional numeric fields
      const dataToSend = {
        ...formData,
        minPrice: formData.minPrice === '' ? undefined : formData.minPrice,
        maxPrice: formData.maxPrice === '' ? undefined : formData.maxPrice
      };
      await instanceAxios.post(businessAdsUrl, dataToSend);
      setSuccess(true);
      // No longer calling handleNext()
    } catch (err) {
      setError('התרחשה שגיאה במהלך שליחת הטופס. אנא נסה שוב.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getValidationErrors = () => {
    const errors: string[] = [];
    switch (activeStep) {
      case 0:
        if (!formData.businessName.trim()) errors.push('שם העסק');
        if (!formData.businessEmail.trim()) errors.push('אימייל העסק');
        else if (!isValidEmail(formData.businessEmail)) errors.push('אימייל תקין');
        if (!formData.businessPhone.trim()) errors.push('טלפון העסק');
        else if (!isValidPhone(formData.businessPhone)) errors.push('טלפון תקין');
        break;
      case 1:
        if (!formData.category.trim()) errors.push('קטגוריה');
        break;
      case 2:
        if (!formData.shortDescription.trim()) errors.push('תיאור קצר');
        break;
      case 3:
        if (formData.location.length === 0) errors.push('מיקום');
        if (formData.targetAudience.length === 0) errors.push('קהל יעד');
        break;
      case 4:
        if (!agreed) errors.push('אישור תקנון');
        break;
    }
    return errors;
  };

  const isStepValid = () => {
    return getValidationErrors().length === 0;
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <BusinessInfoForm formData={formData} setFormData={setFormData} isShaliach={true} />;
      case 1:
        return (
          <CategorySelector
            selectedCategory={formData.category}
            setSelectedCategory={(cat: string) => setFormData(prev => ({ ...prev, category: cat, subCategory: [] }))}
            selectedSubcategory={formData.subCategory}
            setSelectedSubcategory={(subCat: string[]) => setFormData(prev => ({ ...prev, subCategory: subCat }))}
          />
        );
      case 2:
        return <DescriptionAndMediaForm formData={formData} setFormData={setFormData} />;
      case 3:
        return <AdditionalInfoForm formData={formData} setFormData={setFormData} />;
      case 4:
        return <RegulationsAndApproval agreed={agreed} setAgreed={setAgreed} />;
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Box sx={{ textAlign: 'center', mb: '0.35em' }}>
        <Typography variant="h1">הוספת עסק/שירות (בניהול שליח)</Typography>
      </Box>
      <Box sx={{ textAlign: 'center', mb: 2, whiteSpace: 'pre-wrap' }}>{SHALIACH_EXPLANATION_TEXT}</Box>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="body">
          {SHALIACH_REFERRAL_LINE_1}
          <br />
          {SHALIACH_REFERRAL_LINE_2_PREFIX}
          <Link
            href={`${typeof window !== 'undefined' ? window.location.origin : ''}${PUBLISHER_SUBMIT_PATH}`}
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
          >
            לינק המצורף
          </Link>
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Link href={SHALIACH_REGULATIONS_LINK} target="_blank" rel="noopener noreferrer" underline="always">
          תקנון לוח חב"ד - פרסום בבעלות שליח
        </Link>
      </Box>

      <WhatsAppSupportButton />

      {success ? (
        <SubmissionSuccess
          title="המודעה נשלחה בהצלחה!"
          message="פרטי העסק ששלחת ייבדקו על ידי מנהלי המערכת ויופיע בלוח בקרוב."
          onSubmitAnother={handleReset}
          submitAnotherText="לפרסום מודעה נוספת"
        />
      ) : (
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} steps={steps} />
          </Box>

          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button variant="outlined" disabled={activeStep === 0} onClick={handleBack} fullWidth={false}>
              הקודם
            </Button>
            <Button
              onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              disabled={!isStepValid()}
              loading={loading}
              fullWidth={false}
            >
              {activeStep === steps.length - 1 ? 'צור מודעה' : activeStep === steps.length - 2 ? 'המשך' : 'הבא'}
            </Button>
          </Box>

          {error && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="error.main">
                {error}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default ShaliachAdSubmissionPage;
